import { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";

/* Convert #rrggbb to rgba() with alpha */
const hexA = (hex, a) => {
  if (!hex || hex[0] !== "#") return `rgba(255,170,0,${a})`;
  const h = hex.length === 4 ? hex.replace(/#(.)(.)(.)/, "#$1$1$2$2$3$3") : hex;
  const n = parseInt(h.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

const Smoke = ({ color, width }) => (
  <div style={{ position: "absolute", right: "68%", top: "38%", pointerEvents: "none" }} aria-hidden>
    {[0, 1, 2, 3, 4].map((i) => (
      <motion.span
        key={i}
        initial={{ opacity: 0, scale: 0.4, x: 0 }}
        animate={{ opacity: [0, 0.45, 0], scale: [0.4, 1.4], x: [-8, -width * 0.55] }}
        transition={{ duration: 1.7, repeat: Infinity, delay: i * 0.33, ease: "easeOut" }}
        style={{
          position: "absolute",
          width: width * 0.3,
          height: width * 0.3,
          borderRadius: "50%",
          background: `radial-gradient(circle, ${hexA(color, 0.55)}, transparent 70%)`,
          filter: "blur(9px)",
        }}
      />
    ))}
  </div>
);

const Sparkles = ({ color, width }) => {
  const stars = useMemo(
    () => Array.from({ length: 14 }).map(() => ({
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 1.3,
      size: 3 + Math.random() * 5,
    })),
    [],
  );
  return (
    <div aria-hidden>
      {stars.map((st, i) => (
        <motion.span
          key={i}
          animate={{ opacity: [0, 1, 0], scale: [0, 1, 0] }}
          transition={{ duration: 1.1, repeat: Infinity, delay: st.delay, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: `${st.top}%`,
            left: `${st.left}%`,
            width: st.size,
            height: st.size,
            borderRadius: "50%",
            background: color,
            boxShadow: `0 0 ${st.size * 2.2}px ${color}`,
          }}
        />
      ))}
    </div>
  );
};

const LottieAsset = ({ url, width }) => {
  useEffect(() => {
    if (!window.customElements?.get?.("lottie-player")) {
      const s = document.createElement("script");
      s.src = "https://unpkg.com/@lottiefiles/lottie-player@latest/dist/lottie-player.js";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);
  return <lottie-player src={url} autoplay loop style={{ width }} />;
};

/*
 * Renders ONE flying seasonal character over the page (or inside an editor stage).
 * - inline=false  -> position:fixed overlay for the live website
 * - inline=true   -> position:absolute inside a relative parent (editor preview)
 */
export const AnimationPlayer = ({
  preset,
  device = "desktop",
  inline = false,
  previewScale = 1,
  soundOn = false,
  onComplete,
}) => {
  const geo = (device === "mobile" ? preset.mobile : preset.desktop) || preset.desktop || {};
  const effects = preset.effects || {};
  const duration = Math.max(1, Number(preset.duration) || 5);
  const baseOpacity = preset.opacity ?? 1;
  const audioRef = useRef(null);

  const pts = geo.path && geo.path.length >= 2
    ? geo.path
    : [
        { x: geo.startX ?? 10, y: geo.startY ?? 15 },
        { x: geo.endX ?? 90, y: geo.endY ?? 85 },
      ];
  const lefts = pts.map((p) => `${p.x}%`);
  const tops = pts.map((p) => `${p.y}%`);
  const posTimes = pts.map((_, i) => i / (pts.length - 1));
  const rot = Number(geo.rotation) || 0;

  const times = [0, 0.14, 0.86, 1];
  const opable = [
    preset.entrance === "fade" ? 0 : baseOpacity,
    baseOpacity,
    baseOpacity,
    preset.exit === "fade" ? 0 : baseOpacity,
  ];
  const scaleKf = [
    preset.entrance === "zoom" ? 0.3 : 1,
    1,
    1,
    preset.exit === "zoom" ? 0.3 : 1,
  ];

  const width = (Number(geo.width) || 360) * previewScale;
  const g = Number(effects.glow ?? 0);
  const glowColor = effects.glow_color || "#ffaa00";
  const filterParts = [];
  if (g > 0) filterParts.push(`drop-shadow(0 0 ${Math.round(g * 26)}px ${glowColor})`, `drop-shadow(0 0 ${Math.round(g * 54)}px ${glowColor})`);
  if (effects.shadow) filterParts.push("drop-shadow(0 18px 22px rgba(0,0,0,0.35))");
  const filter = filterParts.join(" ") || "none";

  useEffect(() => {
    const t = setTimeout(() => onComplete && onComplete(), duration * 1000 + 180);
    return () => clearTimeout(t);
  }, [duration, onComplete]);

  useEffect(() => {
    const el = audioRef.current;
    if (soundOn && preset.sound?.enabled && preset.sound?.url && el) {
      el.volume = Math.min(1, Math.max(0, preset.sound.volume ?? 0.5));
      el.play().catch(() => {});
    }
  }, [soundOn, preset.sound]);

  const asset = preset.asset || {};
  const type = asset.type || "image";
  const renderAsset = () => {
    if (!asset.url) {
      return <div style={{ width, height: width * 0.6 }} className="rounded-2xl bg-white/10 border border-dashed border-white/30" />;
    }
    if (["webm", "mp4", "video"].includes(type)) {
      return <video src={asset.url} autoPlay muted={!soundOn} loop playsInline style={{ width, filter }} />;
    }
    if (type === "lottie") return <LottieAsset url={asset.url} width={width} />;
    return <img src={asset.url} alt={preset.name || "seasonal animation"} style={{ width, filter }} draggable={false} />;
  };

  const wrapperPos = inline ? "absolute" : "fixed";

  return (
    <div
      className={`${wrapperPos} inset-0 pointer-events-none overflow-hidden`}
      style={{ zIndex: inline ? 5 : 60 }}
      data-testid="seasonal-anim-stage"
    >
      <motion.div
        initial={{ left: lefts[0], top: tops[0], opacity: opable[0], scale: scaleKf[0], rotate: rot }}
        animate={{ left: lefts, top: tops, opacity: opable, scale: scaleKf, rotate: rot }}
        transition={{
          duration,
          left: { duration, times: posTimes, ease: "easeInOut" },
          top: { duration, times: posTimes, ease: "easeInOut" },
          opacity: { duration, times, ease: "linear" },
          scale: { duration, times, ease: "easeOut" },
          rotate: { duration },
        }}
        style={{ position: "absolute", translateX: "-50%", translateY: "-50%" }}
      >
        {effects.smoke && <Smoke color={glowColor} width={width} />}
        {effects.sparkles && <Sparkles color={effects.sparkle_color || "#ffffff"} width={width} />}
        <div style={{ position: "relative" }}>{renderAsset()}</div>
      </motion.div>
      {soundOn && preset.sound?.enabled && preset.sound?.url && <audio ref={audioRef} src={preset.sound.url} />}
    </div>
  );
};
